import { getSocketTicket } from "../api/auth";
import {
  ConversationStatus,
  getHistory,
  hasActiveConversation,
} from "../api/message";
import { HistoryLimit, MANUAL_CLOSE_CODE, SocketErrorCode } from "../constants";
import { errorFileLocalStore } from "../localStore/errorFileManage";
import {
  reporterInstance,
  ReportScene,
  reportScene,
} from "../services/DataReporter";
import {
  autoRetryMessage,
  webSocketService,
} from "../services/WebSocketService";
import { useMessageStore } from "../store";
import {
  CurrentChatStatus,
  HeaderType,
  HeartType,
  Message,
  MessageNotRender,
  MessageSender,
  MessageStatus,
  MessageType,
  SocketConnectionStatus,
  SocketErrorInfoType,
  UploadFileInfo,
} from "../store/types";
import { getDeviceInfo, isHandyApp } from "../utils/device";
import { updateUrlParameter } from "../utils/url";
import Locale from "@/app/locales";
import { authService } from "@/app/services/AuthService";
import { tryParse } from "../utils/object";
import { getFileType, getFileTypeFromFile } from "../utils/file";

const handleHistoryGet = async () => {
  const historyData = await getHistory({
    cursor: "",
    limit: HistoryLimit,
  });
  const msgList = useMessageStore.getState().msgList;
  const lastMsgInMsgList = msgList[msgList.length - 1];
  if (lastMsgInMsgList && historyData.records[0].id !== lastMsgInMsgList.id) {
    useMessageStore.getState().initializeHistory(historyData.records);
    useMessageStore.getState().setLastHistoryMsg({
      hasMore: historyData.hasMore,
      nextCursor: historyData.nextCursor,
    });
  }
};

interface WebSocketOptions {
  url: string; // WebSocket服务器地址
  reconnectInterval?: number; // 断线重连间隔（毫秒）
  maxReconnectAttempts?: number; // 最大重连次数
  ackTimeout?: number; // ACK确认超时时间
  maxRetries?: number; // 消息最大重试次数
  onReadyStateChange?: (state: string) => void; // 连接状态变化回调
  onMessage?: (data: Message) => void; // 消息接收回调
  onError?: (error: Event) => void; // 错误处理回调
  onClose?: (event: CloseEvent) => void; // 连接关闭回调
  onReconnect?: (attempt: number) => void; // 重连尝试回调
  onMessageAck?: (clientId: string, ackMessage?: Message) => void; // 消息确认回调
  onMessageFailure?: (msgId: string) => void; // 消息发送失败回调
  onCommonMessageHandler?: (msg: Message) => void;
}

class IMWebSocketClient {
  private socket: WebSocket | null = null;
  private reconnectCount = 0;
  private isManualClose = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private messageQueue: Map<string, Partial<Message>> = new Map(); // 未确认消息队列
  private pendingAckTimers: Map<string, ReturnType<typeof setTimeout>> =
    new Map(); // ACK等待计时器

  private status: SocketConnectionStatus | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private pingTimeout: NodeJS.Timeout | null = null;
  // 发送心跳间隔时间
  private heartInterval: number = 20000;
  // 上一次pong时间
  private lastPongTime: number | null = null;
  // 上一次ping时间
  private lastPingTime: number = Date.now();

  private isReconnect: boolean = false;

  constructor(private options: WebSocketOptions) {
    // 设置默认值
    this.options.reconnectInterval = this.options.reconnectInterval || 3000;
    this.options.maxReconnectAttempts = this.options.maxReconnectAttempts || 10;
    this.options.ackTimeout = this.options.ackTimeout || 5000;
    this.options.maxRetries = this.options.maxRetries || 3;
    this.connect();
  }

  // 初始化连接
  public async connect(isReconnect = false): Promise<void> {
    if (
      this.status === SocketConnectionStatus.CONNECTING ||
      this.status === SocketConnectionStatus.CONNECTED
    ) {
      return;
    }
    try {
      this.updateStatus(SocketConnectionStatus.CONNECTING);
      // 清理现有连接
      if (this.socket) {
        this.socket.onopen = null;
        this.socket.onmessage = null;
        this.socket.onerror = null;
        this.socket.onclose = null;
        this.socket = null;
      }
      let socketUrl = this.options.url;
      this.isReconnect = isReconnect;
      if (isReconnect) {
        // 如果来源于重连，则需要重新生成ticket
        const { ticket } = await getSocketTicket();
        if (ticket) {
          socketUrl = updateUrlParameter(
            this.options.url,
            "ticket",
            ticket as string,
          );
        }
      }
      // 开始创建Websocket 链接
      reportScene(
        ReportScene.CREATE_WEBSOCKET,
        JSON.stringify({
          socketUrl,
          isReconnect,
          device: getDeviceInfo(),
          isHandyApp: isHandyApp(),
        }),
      );
      this.socket = new WebSocket(socketUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error("WebSocket初始化失败:", error);
      this.handleReconnect();
      this.updateStatus(SocketConnectionStatus.ERROR);
    }
  }

  /**
   * 补偿发送失败的消息
   */
  private async handleErrorMsgInsert() {
    const errorFile = errorFileLocalStore.get() as Record<
      string,
      UploadFileInfo
    >;
    if (errorFile) {
      for (const fileId in errorFile) {
        const fileInfo = errorFile[fileId];
        if (fileInfo.file) {
          const fileType = await getFileTypeFromFile(fileInfo.file);
          webSocketService.sendMessage(
            `{"fileName":"${fileInfo.file.name}", "fileUrl": "${URL.createObjectURL(fileInfo.file)}", "fileId": "${fileId}"}`,
            {
              msgType: fileType,
              noNeedSendToWebsocket: true,
              senderType: MessageSender.USER,
              status: MessageStatus.ERROR,
              clientId: fileId,
              fileInfo,
            },
          );
        }
        errorFileLocalStore.remove(fileId);
      }
    }
    for (const [, msg] of this.messageQueue) {
      if (
        msg.content &&
        msg.clientId &&
        msg.senderType === MessageSender.USER
      ) {
        webSocketService.sendMessage(msg.content, {
          msgType: msg.type,
          noNeedSendToWebsocket: true,
          senderType: msg.senderType,
          status: MessageStatus.ERROR,
        });
        this.messageQueue.delete(msg.clientId);
      }
    }
  }

  private async handleReconnectCallback() {
    if (this.isReconnect) {
      try {
        // 如果有发送中的消息，则不能立即拉取历史记录, 若尝试4次失败，则拉取历史记录
        await autoRetryMessage(() => {
          const { msgList } = useMessageStore.getState();
          return msgList.every(
            (item) =>
              ![MessageStatus.Pending, MessageStatus.Sending].includes(
                item.status as MessageStatus,
              ),
          );
        }, 5);
        await handleHistoryGet();
        // 补充发送失败的消息
        try {
          this.handleErrorMsgInsert();
        } catch (error) {
          console.error("insert error");
        }
        // 开启socket，需要进行判断当前的状态，可能基于重连后需要回到上一次的状态
        hasActiveConversation().then((sessionInfo) => {
          if (sessionInfo.hasActiveConversation) {
            if (sessionInfo.status === ConversationStatus.LineUp) {
              // 重连成功后，状态为排队中
              useMessageStore
                .getState()
                .setCurrentChatStatus(CurrentChatStatus.LineUp);
              webSocketService.sendMessage(
                JSON.stringify({ text: sessionInfo.queueDesc }),
                {
                  msgType: MessageType.LineUp,
                  noNeedSendToWebsocket: true,
                  senderType: MessageSender.SYSTEM,
                },
              );
            } else {
              // 重连成功后，状态为在线
              useMessageStore
                .getState()
                .setCurrentChatStatus(CurrentChatStatus.Online);
            }
          }
        });
      } catch (error) {}
    }
  }

  // 设置事件处理器
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = async () => {
      this.reconnectCount = 0;
      this.updateStatus(SocketConnectionStatus.CONNECTED);
      this.startHeartbeat();
      this.handleReconnectCallback();
    };

    this.socket.onmessage = (event) => {
      // 监测心跳
      if (event.data === HeartType.PING || event.data === HeartType.PONG) {
        if (event.data === HeartType.PING) {
          this.socket?.send(HeartType.PONG);
        } else {
          this.handlePong();
        }
        return;
      }
      try {
        const message = this.parseMessage(event.data);
        if (!message) {
          this.options.onError?.(new ErrorEvent("message-parse-failed"));
          return;
        }
        this.options.onCommonMessageHandler?.(message);
        if (message.headerType === HeaderType.ACK) {
          if (message.type === MessageType.MsgEnd) {
            // 当消息结束时，无需再更新ack
            this.options.onMessage?.(message);
          } else {
            // 处理ACK确认
            this.handleAck(message);
          }
        } else {
          // 收到非ACK消息时发送ACK确认
          if (message.id) {
            this.sendAck(message);
          }

          // 非ACK消息上报
          reportScene(
            ReportScene.AGENT_MSG_RECEIVE,
            JSON.stringify({
              id: message.id,
              type: message.type,
              conversationId: message.conversationId,
            }),
          );

          // 调用onMessage回调
          this.options.onMessage?.(message);
        }
      } catch (error) {
        console.error("消息处理失败:", error);
        this.options.onError?.(new ErrorEvent("message-parse-failed"));
      }
    };

    this.socket.onerror = (event) => {
      console.error("WebSocket错误:", event);
      this.options.onError?.(event);
      this.updateStatus(SocketConnectionStatus.ERROR);
      this.stopHeartbeat();
      reportScene(ReportScene.SOCKET_ONERROR, JSON.stringify({ event }));
    };

    this.socket.onclose = (event) => {
      if (event.wasClean) {
        console.info(
          `socket onclose code: ${event.code}, reason: ${event.reason || "empty"}`,
        );
      } else {
        console.info("socket onclose accident", "system");
      }
      if (
        this.isManualClose ||
        [
          MANUAL_CLOSE_CODE,
          SocketErrorCode.AuthenticationFailed,
          SocketErrorCode.BlackList,
          SocketErrorCode.SessionExpired,
          SocketErrorCode.ManagerForceOutLine,
          SocketErrorCode.CancelLineUpByUser,
          SocketErrorCode.CloseChatByUser,
          SocketErrorCode.LineUpTimeout,
          SocketErrorCode.ChatTimeout,
          SocketErrorCode.CloseChatByAgent,
          SocketErrorCode.AgentOutOfService,
          SocketErrorCode.LoggedInOtherPlace,
          // 服务异常
          SocketErrorCode.ServiceError,
          // 消息太大
          SocketErrorCode.MessageTooBig,
          // 服务限流
          SocketErrorCode.ServiceLimitStream,
          // 业务异常
          SocketErrorCode.BusinessError,
        ].includes(event.code)
      ) {
        // socket断开了, 不重连
        this.updateStatus(SocketConnectionStatus.DISCONNECTED);
      } else {
        console.log("onerror 尝试重连");
        // 尝试重连
        this.handleReconnect();
        this.updateStatus(SocketConnectionStatus.CLOSE);
      }
      if ([SocketErrorCode.BusinessError].includes(event.code)) {
        // 走前端提示，跳转工单
        useMessageStore.getState().setSocketErrorInfo({
          type: SocketErrorInfoType.TEXT,
          message: Locale.chatInput.processError.replace(
            "${0}",
            process.env.NEXT_PUBLIC_WORK_ORDER_LINK as string,
          ),
        });
      }
      // 需要推送给用户
      if ([SocketErrorCode.BlackList].includes(event.code)) {
        useMessageStore.getState().setSocketErrorInfo({
          type: SocketErrorInfoType.TEXT,
          message: event.reason || Locale.chatInput.networkLost,
        });
      }
      // 需要推送给用户
      if (event.code === SocketErrorCode.AgentOutOfService) {
        // 关闭时返回不在客服时间，即使绕过链接Websocket 依然提示不在客服时间
        useMessageStore.getState().setSocketErrorInfo({
          type: SocketErrorInfoType.TEXT,
          message: event.reason || Locale.systemMsg.currentTimeNotService,
        });
      }
      reportScene(
        ReportScene.SOCKET_ONCLOSE,
        `code: ${event.code}, reason: ${event.reason}`,
      );
      // 当出现socket close 则立即上报
      reporterInstance.flush();
      this.stopHeartbeat();
      this.options.onClose?.(event);
    };
  }
  // 停止心跳检测
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
  }
  // 开始心跳检测
  startHeartbeat() {
    // 开始心跳
    this.sendPing();

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // 设置定时器定期发送心跳
    this.heartbeatInterval = setInterval(() => {
      this.sendPing();
    }, this.heartInterval);
  }

  // 处理pong响应
  handlePong() {
    this.lastPongTime = Date.now();

    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
  }

  sendPing() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      // 在发送心跳前检查token一致性
      this.checkTokenConsistency();

      this.socket.send(HeartType.PING);
      this.lastPingTime = Date.now();
      authService.renewalToken();
      // 设置超时检测
      this.pingTimeout = setTimeout(() => {
        if (!this.lastPongTime || this.lastPingTime > this.lastPongTime) {
          reportScene(
            ReportScene.HEART_TIMEOUT,
            JSON.stringify({
              lastPingTime: this.lastPingTime,
              lastPongTime: this.lastPongTime,
            }),
          );
        }
      }, 10000);
    }
  }

  // 检查token一致性
  private checkTokenConsistency(): void {
    try {
      const cookieToken = authService.getToken();
      const loginToken = authService.getLoginToken();

      // 如果cookie中没有token，说明用户已登出
      if (!cookieToken) {
        console.log("Token检查: cookie中没有token，用户已登出");
        this.handleTokenMismatch();
        return;
      }

      // 如果没有登录token，说明还未完成登录
      if (!loginToken) {
        console.log("Token检查: 尚未完成登录流程");
        return;
      }

      // 如果两个token不一致，说明可能在其他地方重新登录了
      if (cookieToken !== loginToken) {
        console.log(
          "Token变更: 检测到token已更新，可能是其他标签页登录了新账号",
        );
        this.handleTokenMismatch();
        return;
      }

      // Token一致，继续正常流程
    } catch (error) {
      console.error("Token一致性检查失败:", error);
    }
  }

  // 处理token不匹配的情况
  private handleTokenMismatch(): void {
    try {
      console.log("处理token不匹配: 结束会话");

      // 使用AuthService清理token
      authService.clearToken();

      // 关闭WebSocket连接
      if (this.socket) {
        this.socket.close(MANUAL_CLOSE_CODE, "Token不一致，结束会话");
      }

      // 清理相关状态
      this.stopHeartbeat();
      this.updateStatus(SocketConnectionStatus.DISCONNECTED);

      // 设置错误信息
      // useMessageStore.getState().setSocketErrorInfo({
      //   type: SocketErrorInfoType.TEXT,
      //   message: "登录状态已失效，请重新登录",
      // });
    } catch (error) {
      console.error("处理token不匹配时发生错误:", error);
    }
  }

  // 统一消息发送方法
  private send(data: Partial<Message>): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error("消息发送失败: 连接未就绪");
      return;
    }

    const payload = typeof data === "string" ? data : JSON.stringify(data);
    this.socket.send(payload);
  }

  // 发送消息（支持自动ACK确认）
  public sendMessage(message: Message): string {
    const clientId = message.clientId as string;
    // 添加到待确认队列(实时消息不加入队列)
    if (message.type !== MessageNotRender.RealTime) {
      this.messageQueue.set(clientId, message);
    }

    // 发送消息
    this.send(message);

    // 启动ACK等待计时器
    this.setupAckTimeout(clientId);

    return clientId;
  }

  // 发送ACK确认
  private sendAck(msg: Message): void {
    const ackConfirm = {
      ...msg,
      headerType: HeaderType.ACK,
      clientTimestamp: Date.now(),
    };
    this.send(ackConfirm);
  }

  // 处理ACK确认消息
  private handleAck(ackMessage: Message): void {
    const clientId = ackMessage.clientId as string;
    // ack 确认
    reportScene(
      ReportScene.ACK_CONFIRM,
      JSON.stringify({
        id: ackMessage.id,
        clientId,
      }),
    );

    // 清除计时器
    this.clearAckTimeout(clientId);

    // 从未确认队列中移除
    this.messageQueue.delete(clientId);

    // 触发回调
    this.options.onMessageAck?.(clientId, ackMessage);
  }

  // 设置ACK超时处理
  private setupAckTimeout(clientId: string): void {
    if (!this.messageQueue.has(clientId)) return;

    const message = this.messageQueue.get(clientId)!;
    const retryCount = message.retryCount || 3;

    const timer = setTimeout(() => {
      if (!this.messageQueue.has(clientId)) {
        // 消息已被确认，无需处理
        return;
      }
      // 检查是否超过重试次数
      if (retryCount >= this.options.maxRetries!) {
        // 消息发送彻底失败
        // this.messageQueue.delete(clientId);
        this.options.onMessageFailure?.(clientId);
        return;
      }

      // 更新重试次数
      message.retryCount = (message.retryCount || 0) + 1;
      message.timestamp = Date.now();
      this.messageQueue.set(clientId, message);

      console.warn(
        `消息${clientId}未确认，尝试重发(${message.retryCount}/${this.options.maxRetries})`,
      );

      // 尝试重发消息
      this.send(message);

      // 重新设置超时
      this.setupAckTimeout(clientId);
    }, this.options.ackTimeout);

    this.pendingAckTimers.set(clientId, timer);
  }

  // 清除ACK等待计时器
  private clearAckTimeout(msgId: string): void {
    const timer = this.pendingAckTimers.get(msgId);
    if (timer) {
      clearTimeout(timer);
      this.pendingAckTimers.delete(msgId);
    }
  }

  // 断线重连机制
  public async handleReconnect() {
    if (this.isManualClose) return;

    try {
      const sessionInfo = await hasActiveConversation();
      // 尝试重连前，若用户不在会话，则不重连
      if (!sessionInfo.hasActiveConversation) {
        // 虽然不重连了，但是依然要拉取历史数据
        await handleHistoryGet();
        this.updateStatus(SocketConnectionStatus.DISCONNECTED);
        return;
      }
    } catch (error) {
      console.error("重连时，网络依然不通");
    }

    if (this.reconnectCount >= this.options.maxReconnectAttempts!) {
      console.error(
        `已达到最大重连次数(${this.options.maxReconnectAttempts})，停止重连`,
      );
      this.updateStatus(SocketConnectionStatus.DISCONNECTED);
      return;
    }

    this.clearReconnectTimer();
    this.reconnectCount++;

    this.reconnectTimer = setTimeout(() => {
      this.options.onReconnect?.(this.reconnectCount);
      console.log("reconnectTimer", this.reconnectCount);
      this.connect(true);
    }, this.options.reconnectInterval);
  }

  // 消息解析
  private parseMessage(data: string): Message | null {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  // 更新连接状态
  private updateStatus(newStatus: SocketConnectionStatus): void {
    if (
      [
        SocketConnectionStatus.DISCONNECTED,
        SocketConnectionStatus.ERROR,
      ].includes(newStatus)
    ) {
      // 结束了（会话结束了）
      useMessageStore
        .getState()
        .setCurrentChatStatus(CurrentChatStatus.ChatEnd);
    }
    useMessageStore.getState().setConnectionStatus(newStatus);
    this.status = newStatus;
    this.options.onReadyStateChange?.(this.status);
  }

  // 安全关闭连接
  public close(code?: number, reason?: string): void {
    this.isManualClose = code === MANUAL_CLOSE_CODE;
    // 清除所有ACK计时器
    this.pendingAckTimers.forEach((timer) => clearTimeout(timer));
    this.pendingAckTimers.clear();

    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN) {
        // 关闭连接
        this.socket.close(code || MANUAL_CLOSE_CODE, reason || "用户主动关闭");
      } else {
        this.socket = null;
        this.updateStatus(SocketConnectionStatus.CLOSE);
      }
    }
  }

  // 获取连接状态
  public getStatus(): SocketConnectionStatus | null {
    return this.status;
  }

  // 清理重连计时器
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

export default IMWebSocketClient;
